/**
 * Which videos become a PLAYER, and the one size ceiling a video gets.
 *
 * Both answers are shared across every surface that accepts a video —
 * card descriptions, card + wiki comments, chat, the wiki
 * doc body and its Drive-style file nodes — because a clip a user can
 * post in one place must be postable in the others, and must render the
 * same way. Before this module each surface carried its own copy of both
 * facts and they had drifted: the wiki took 200 MiB while cards and chat
 * refused the same file at 100 MB.
 *
 * The MIME set is deliberately NOT `video/*`. It is the three containers
 * a browser `<video>` element plays natively; everything else (mkv, avi,
 * …) stays an inline file chip, which is the honest render for bytes the
 * page cannot play. It mirrors the BE `PLAYABLE_VIDEO_MIME_TYPES` in
 * `3mote-be-new/src/schemas/attachments.ts` — the authoritative gate,
 * since a client check only decides how fast the user hears "no".
 */

/** Containers a browser `<video>` plays natively. Other `video/*` → file chip. */
export const VIDEO_EMBED_MIME_TYPES: ReadonlySet<string> = new Set([
  'video/mp4',
  'video/webm',
  'video/quicktime',
])

export function isVideoEmbedMime(mime: string): boolean {
  return VIDEO_EMBED_MIME_TYPES.has(mime)
}

/**
 * The `accept` attribute for a video file picker.
 *
 * Derived from the set above so the two cannot drift, and it carries the
 * file EXTENSIONS as well as the MIME types on purpose: a picker matches
 * by whichever it is given, and some platforms hand a `.mov` an empty or
 * surprising type. Extensions are not derivable from a MIME string, so
 * that half is written out — keep it adjacent to the set it mirrors, and
 * remember `accept` is a hint the OS may ignore, never a gate. The real
 * gate is the server.
 */
const VIDEO_EMBED_EXTENSIONS = ['.mp4', '.webm', '.mov'] as const

export const VIDEO_EMBED_ACCEPT_ATTR = [...VIDEO_EMBED_MIME_TYPES, ...VIDEO_EMBED_EXTENSIONS].join(
  ',',
)

/**
 * The one video ceiling, on every surface: 200 MiB.
 *
 * Binary MiB, matching the BE `MAX_VIDEO_BYTES`, the `attachments`
 * column CHECK and the presign schema's `sizeBytes` bound — all three are
 * exactly 209,715,200, and a decimal 200 MB here would refuse the top
 * ~9.7 MB of the range the server accepts. Copy that names the limit says
 * "200 MB" because that is what the number is called, the way
 * `formatBytes` labels every other size.
 *
 * Raising this needs multipart upload first: the transport is a single
 * browser PUT.
 */
export const VIDEO_ATTACHMENT_MAX_BYTES = 200 * 1024 * 1024
